import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  getConfigByApp,
  setQueryParams,
  utilServerSideDeviceDetection,
} from '@utils';
import {metaInfo} from '@configs';
import {JOBFEED, ROUTE_JOB_DETAIL} from '@constants';
import type {GetServerSidePropsContext} from 'next/types';
import {SAVEDJOBS} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {
  findSavedJobs,
  saveJob,
} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {SavedJobsQR} from '@jobseeker/lotus/assets/images';
import {memo, useEffect, useState} from 'react';
import {openUrl} from '@jobseeker/common-core/utils/common';
import dynamic from 'next/dynamic';
import Loader from '@jobseeker/lotus/components/Loader';
import {isPlural} from '@jobseeker/lotus/utils/date';
import type {AppState, Job} from '@jobseeker/common-core/types';
import debounce from 'lodash/throttle';
import {useRouter} from 'next/router';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {BannerInfo, HiristBanner} from '@jobseeker/lotus/components';
import {Box, Grid, Typography} from '@mui/material';
import {SavedHirist} from '@jobseeker/lotus/assets/icons/index';
import {InjectSearchOverlay} from '@components';
import {handleTracking} from '@jobseeker/common-core/utils';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {AppDownloadBanner} from '@jobseeker/lotus/components';

const ListWithTitleHarness = memo(
  dynamic(
    () =>
      import('@jobseeker/client-harness/components/JobHarness/ListWithTitle'),
    {
      loading: () => <Loader />,
    }
  )
);

const ProfileCardHarness = dynamic(
  () => import('@jobseeker/client-harness/components/ProfileHarness'),
  {
    ssr: false,
  }
);

const DownloadAppJobdetail = dynamic(
  () => import('@jobseeker/lotus/components/DownloadAppJobdetailAndCatogary'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);

const CTA = dynamic(() => import('@jobseeker/lotus/components/CTA'), {
  loading: () => <Loader />,
  ssr: false,
});

type SavedJobsProps = {
  seoConfig: object;
  showSavedIcon: boolean;
  isMobile: boolean;
};

const SavedJobsPage = ({seoConfig, isMobile}: SavedJobsProps) => {
  const [pageMaps, setPageMaps] = useState<number[]>([]);
  const [page, setPage] = useState<number>(0);
  const [concat, setConcat] = useState<boolean>(false);
  const {isLoggedIn, userId} = useSelector((state: AppState) => state.auth);
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, 'saved-jobs');
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const isAboveMobile = useIsLargeDevice('sm');
  const isLarge = useIsLargeDevice(1025);
  const mulWidget = metaData?.jobfeed?.mulWidget;
  const earlyApplicant = metaData?.premiumProducts?.earlyApplicant;
  const {
    profile: {subscriptions},
  } = useSelector((state: AppState) => state.user);

  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  useEffect(() => {
    try {
      dispatch(findSavedJobs({page, concat}));
    } catch (error) {
      console.log('error', error);
    }
  }, [page]);

  const debounceOptions = {leading: true};

  const router = useRouter();
  useEffect(() => {
    const sessionUserId = sessionStorage.getItem('userId') || '-1';

    TrackEventUBA('savedJobsPageView', 'view', {
      ref: router.query.ref || '',
      pageName: 'Saved Jobs',
      sessionUsername: sessionUserId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPageQuery = debounce(
    (page: number) => {
      const initialPage = 0;
      const pageNo = page + initialPage;
      if (pageNo <= 1) {
        setQueryParams({page: null});
      } else setQueryParams({page: pageNo});
    },
    250,
    debounceOptions
  );

  const onScrollNew = () => {
    if (pageMaps.length) {
      let pageNo = 0;
      for (let i = 0; i <= pageMaps.length; i++) {
        if (pageMaps[i] < document.documentElement.scrollTop) {
          pageNo = pageNo + 1;
        } else {
          break;
        }
      }
      setPageQuery(pageNo + 1);
    }
  };

  const {isLoading} = useSelector((state: AppState) => state.job);
  const {savedJobCount} = useSelector((state: AppState) => state.user);

  const onJobSave = (jobData: Job, unsave: boolean) => {
    if (isLoggedIn) {
      dispatch(
        saveJob({
          ...jobData,
          jobId: jobData.id,
          unsave,
          userId: userId,
        })
      );
    }
  };

  const routeToJobDetail = (jobSlug: string, jobId: string | number) => {
    openUrl(`${ROUTE_JOB_DETAIL}/${jobSlug}-${jobId}`);
  };

  const onLoadMore = (event: Event, target: HTMLElement) => {
    pageMaps.push(target.scrollTop || document.documentElement.scrollTop);
    setPageMaps(pageMaps);
    setPage(page + 1);
    setConcat(true);
  };

  const sendEvent = (
    eventCatogary: string,
    eventName: string,
    action: string
  ) => {
    handleTracking(`${eventName}_${action}`, {
      event_category: `${eventCatogary}`,
      Origin: page,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    });
  };

  const filteredMulWidget = mulWidget?.content?.filter(
    (info: {tag?: string}) => {
      if (info?.tag === 'earlyapplicant' && earlyApplicant?.enabled) {
        if (subscriptions?.earlyApplicant?.isValid) {
          return false;
        }
        return true;
      }

      return true;
    }
  );

  const breadcrumbElements = !isAppIimjobs
    ? []
    : [
        {
          name: 'Home',
          item: '/',
        },
        {
          name: 'Saved Jobs',
          item: '/saved-jobs',
        },
      ];

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={breadcrumbElements}
          variant={VersionVariants.VERSION_TWO}
        >
          {!isAppIimjobs && (
            <Box
              // mt={{xxs: -1, md: 4}}
              mb={4}
            >
              <HiristBanner>
                <BannerInfo
                  Icon={SavedHirist}
                  isMobile={!!isMobile}
                  title={'Your Saved Jobs'}
                  subTitle={`Keep all your relevant jobs in saved folder, you can
                      Apply/Remove them anytime!!`}
                />
              </HiristBanner>
            </Box>
          )}

          <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />

          <Grid
            sx={{position: 'relative'}}
            container
            rowSpacing={2}
            columnSpacing={isAppIimjobs ? 2 : {md: 1, lg: 2, xl: 4}}
            pl={!isAppIimjobs ? {xxs: 0, md: 4} : 0}
            pr={!isAppIimjobs ? (isLarge ? 6 : {xxs: 0, md: 4}) : 0}
          >
            <Grid
              item
              sx={{
                width: !isAppIimjobs
                  ? isLarge
                    ? 'calc(100% - min(27.66%, 352px))'
                    : '100%'
                  : '100%',
              }}
            >
              <Box
                sx={{
                  mx: isAppIimjobs ? {lg: 12.5, xl: 17.5} : 0,
                  mb: 1,
                  display: !isAppIimjobs ? 'none' : {xxs: 'none', md: 'block'},
                }}
              >
                <AppDownloadBanner
                  title="Never Miss an Opportunity: Your Saved Jobs, Smartly Notified!"
                  subtitle="Download the iimjobs App to get smart notifications for your saved jobs and never miss an opportunity"
                  qrSize={70}
                  maxWidthLeft={520}
                  QR={SavedJobsQR}
                />
              </Box>
              <Box>
                <ListWithTitleHarness
                  injectAsChild={false}
                  showThreeDotMenu={true}
                  onJobSave={onJobSave}
                  onInfiniteScroll={onScrollNew}
                  showSaveIcon={false}
                  routeToJobDetail={routeToJobDetail}
                  infiniteScroll={true}
                  onLoadMore={onLoadMore}
                  title={'Saved Jobs'}
                  subTitle={`${
                    isLoading === null
                      ? ''
                      : savedJobCount && savedJobCount > 0
                      ? `${savedJobCount} Job${isPlural(
                          savedJobCount
                        )} saved by you`
                      : 'No Job saved by you'
                  } `}
                />
              </Box>
            </Grid>
            {!isAppIimjobs && (
              <Grid
                item
                sx={{
                  display: isLarge ? 'block' : 'none',
                  width: isLarge ? 'min(27.66%,352px)' : '0%',
                }}
              >
                <Box
                  sx={{
                    mb: 3.5,
                  }}
                >
                  <Typography
                    fontSize={16}
                    fontWeight={500}
                    color="secondary.main"
                    sx={{my: '16px'}}
                  >
                    Profile Status
                  </Typography>
                  <ProfileCardHarness
                    showSmallBadges={false}
                    variant={VersionVariants.VERSION_ONE}
                  />
                  {isAboveMobile && mulWidget?.enabled && (
                    <CTA
                      cardSxProps={{
                        mt: 2,
                        mb: 3.5,
                        borderRadius: 2,
                      }}
                      cardInfo={filteredMulWidget}
                      variant={VersionVariants.VERSION_FOUR}
                    />
                  )}
                  <DownloadAppJobdetail sendEvent={sendEvent} />
                </Box>
              </Grid>
            )}
          </Grid>
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const metaData = getConfigByApp(metaInfo, JOBFEED, true, 'saved-jobs');
    const slugUrl = context?.resolvedUrl || '';

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: SAVEDJOBS},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const seoConfig = await loadSEO(
      SAVEDJOBS,
      {
        title: '',
        description: '',
      },
      slugUrl,
      true
    );

    return {
      props: {
        isMobile,
        seoConfig,
        metaData,
      },
    };
  }
);

export default SavedJobsPage;
